import { motion } from "framer-motion";
import { Check, X, Target, Activity, Gauge, Layers, Repeat, TrendingUp } from "lucide-react";

const PILLARS = [
  { icon: Target, code: "O", title: "Observe", desc: "Full biomechanical & performance assessment. We measure before we prescribe." },
  { icon: Gauge, code: "R", title: "Rate", desc: "Every athlete is scored against a proven performance index — not guesswork." },
  { icon: Layers, code: "I", title: "Individualize", desc: "Programming built to the athlete, their sport, and their exact deficits." },
  { icon: Activity, code: "G", title: "Groove", desc: "Neuromuscular patterning that makes speed automatic and repeatable." },
  { icon: Repeat, code: "I", title: "Iterate", desc: "Reassess, adjust, and progressively overload. The system evolves with you." },
  { icon: TrendingUp, code: "N", title: "Notch Up", desc: "Compounding, measurable gains — faster splits, higher output, real results." },
];

const COMPARE = {
  them: [
    "Random workouts pulled from social media",
    "One-size-fits-all group sessions",
    "No baseline, no tracking, no proof",
    "Train hard, hope it works",
  ],
  us: [
    "A proprietary 6-stage performance framework",
    "Programming individualized to your data",
    "Objective testing at every checkpoint",
    "Engineered, measurable speed gains",
  ],
};

export default function Origin() {
  return (
    <section
      id="origin"
      data-testid="origin-section"
      className="relative py-16 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6"
          >
            The Difference
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl"
            data-testid="origin-title"
          >
            Everyone sells workouts. <br />
            We built the <span className="text-white/50">ORIGIN™</span> System.
          </motion.h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            ORIGIN™ is our proprietary methodology for developing speed and
            explosiveness. Six connected stages that turn training from a guess
            into a science.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0b0b0b] border border-white/10 p-8 md:p-10"
            data-testid="compare-them"
          >
            <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-white/40 mb-6">
              Everyone Else
            </p>
            <ul className="space-y-4">
              {COMPARE.them.map((t) => (
                <li key={t} className="flex items-start gap-3 text-white/50">
                  <X size={20} className="text-white/25 shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white text-black p-8 md:p-10"
            data-testid="compare-us"
          >
            <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-black/50 mb-6">
              The ORIGIN™ System
            </p>
            <ul className="space-y-4">
              {COMPARE.us.map((t) => (
                <li key={t} className="flex items-start gap-3 text-black">
                  <Check size={20} className="text-black shrink-0 mt-0.5" />
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="mt-20">
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-8">
            O.R.I.G.I.N — Six Stages
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="group bg-black hover:bg-[#0d0d0d] p-8 transition-colors duration-300"
                  data-testid={`origin-pillar-${i}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-display font-black text-6xl text-white/10 group-hover:text-white/30 transition-colors duration-300 leading-none">
                      {p.code}
                    </span>
                    <Icon size={24} className="text-white/70" />
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl mb-2">
                    {p.title}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
